<template>
  <div style="width: 100%; height: 100%; padding: 50px 0 250px 0; overflow-y: scroll; display: flex; flex-direction: column; align-items: center; justify-items: center; gap: 200px;">
    <div class="chart-container">
      <div>
        <p style="font-size: 20px; color: #4B5563; font-weight: normal; margin: 0;">New TCC Applications</p>
        <div style="width: 100%; height: 50px;">
          <PeriodSelector
            :selected="newPeriod"
            @select="onSelectNewPeriod"
          />
        </div>
      </div>
      <Chart
        type="bar"
        :display-title="false"
        :show-legend="false"
        y-axis-title="Count"
        :data="newTcc"
      />
    </div>
    <div class="chart-container">
      <div>
        <p style="font-size: 20px; color: #4B5563; font-weight: normal; margin: 0;">Total TCC Applications</p>
        <div style="width: 100%; height: 50px;">
          <PeriodSelector
            :selected="totalPeriod"
            @select="onSelectTotalPeriod"
          />
        </div>
      </div>
      <Chart
        type="bar"
        :display-title="false"
        :show-legend="false"
        y-axis-title="Count"
        :data="totalTcc"
      />
    </div>
    <div
      v-if="store.state.selectedPartner.id === -1"
      class="chart-container"
    >
      <div style="margin-bottom: 10px;">
        <p style="font-size: 20px; color: #4B5563; font-weight: normal; margin: 0;">New TCC Applications Per Partner</p>
        <div style="width: 100%; height: 50px;">
          <PeriodSelector
            :selected="newPerPartnerPeriod"
            @select="onSelectNewPerPartnerPeriod"
          />
        </div>
        <div style="width: 100%">
          <div style="width: 250px; height: 30px; position: relative;">
            <DateSelector
              :date="newTccDate"
              :min="newPerPartnerMin"
              :max="newPerPartnerMax"
              :period="newPerPartnerPeriod"
              @select="selectNewTccDate"
            />
          </div>
        </div>
      </div>
      <Chart
        type="bar"
        :display-title="false"
        :show-legend="false"
        y-axis-title="Count"
        :data="newTccPerPartner"
      />
    </div>
    <div
      v-if="store.state.selectedPartner.id === -1"
      class="chart-container"
    >
      <div style="margin-bottom: 10px;">
        <p style="font-size: 20px; color: #4B5563; font-weight: normal; margin: 0;">Total TCC Applications Per Partner</p>
        <div style="width: 100%; height: 50px;">
          <PeriodSelector
            :selected="totalPerPartnerPeriod"
            @select="onSelectTotalPerPartnerPeriod"
          />
        </div>
        <div style="width: 100%">
          <div style="width: 250px; height: 30px; position: relative;">
            <DateSelector
              :date="totalTccDate"
              :min="totalPerPartnerMin"
              :max="totalPerPartnerMax"
              :period="totalPerPartnerPeriod"
              @select="selectTotalTccDate"
            />
          </div>
        </div>
      </div>
      <Chart
        type="bar"
        :display-title="false"
        :show-legend="false"
        y-axis-title="Total"
        :data="totalTccPerPartner"
      />
    </div>
  </div>
</template>

<script setup>
import { store } from '@/store'
import { computed, ref, watch } from 'vue'
import Chart from '@/components/Chart.vue'
import PeriodSelector from '@/components/PeriodSelector.vue'
import DateSelector from '@/components/DateSelector.vue'
import _ from 'lodash'

const newPeriod = ref('Monthly')
const totalPeriod = ref('Monthly')
const newPerPartnerPeriod = ref('Monthly')
const totalPerPartnerPeriod = ref('Monthly')
const newTccDate = ref(store.state.reportEndDate)
const totalTccDate = ref(store.state.reportEndDate)

const totalPerPartnerMin = computed(() => {
  if (totalPerPartnerPeriod.value === 'Monthly') {
    return _.min(store.state.monthlyReportResponse.totalTccMonthly.map(x => new Date(x[0].date)))
  } else if (totalPerPartnerPeriod.value === 'Quarterly') {
    return _.min(store.state.monthlyReportResponse.totalTccQuarterly.map(x => new Date(x[0].date)))
  } else {
    return _.min(store.state.monthlyReportResponse.totalTccYearly.map(x => new Date(x[0].date)))
  }
})

const totalPerPartnerMax = computed(() => {
  if (totalPerPartnerPeriod.value === 'Monthly') {
    return _.max(store.state.monthlyReportResponse.totalTccMonthly.map(x => new Date(x[0].date)))
  } else if (totalPerPartnerPeriod.value === 'Quarterly') {
    return _.max(store.state.monthlyReportResponse.totalTccQuarterly.map(x => new Date(x[0].date)))
  } else {
    return _.max(store.state.monthlyReportResponse.totalTccYearly.map(x => new Date(x[0].date)))
  }
})

const newPerPartnerMin = computed(() => {
  if (newPerPartnerPeriod.value === 'Monthly') {
    return _.min(store.state.monthlyReportResponse.tccMonthly.map(x => new Date(x[0].date)))
  } else if (newPerPartnerPeriod.value === 'Quarterly') {
    return _.min(store.state.monthlyReportResponse.tccQuarterly.map(x => new Date(x[0].date)))
  } else {
    return _.min(store.state.monthlyReportResponse.tccYearly.map(x => new Date(x[0].date)))
  }
})

const newPerPartnerMax = computed(() => {
  if (newPerPartnerPeriod.value === 'Monthly') {
    return _.max(store.state.monthlyReportResponse.tccMonthly.map(x => new Date(x[0].date)))
  } else if (newPerPartnerPeriod.value === 'Quarterly') {
    return _.max(store.state.monthlyReportResponse.tccQuarterly.map(x => new Date(x[0].date)))
  } else {
    return _.max(store.state.monthlyReportResponse.tccYearly.map(x => new Date(x[0].date)))
  }
})

const newTcc = computed(() => {
  let data
  let labels
  if (store.state.monthlyReportResponse !== null) {
    if (newPeriod.value === 'Monthly') {
      data = store.state.monthlyReportResponse.tccMonthly
    } else if (newPeriod.value === 'Quarterly') {
      data = store.state.monthlyReportResponse.tccQuarterly
    } else {
      data = store.state.monthlyReportResponse.tccYearly
    }
    if (store.state.selectedPartner.id === -1) {
      data = data.map(x => {
        return {
          id: -1,
          name: 'All',
          date: x[0].date,
          result: x.map(y => y.result).reduce((partialSum, a) => partialSum + a, 0)
        }
      })
    } else {
      data = data.map(x => x.find(y => y.id === store.state.selectedPartner.id))
    }
    labels = data.map(x => new Date(x.date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }))
    data = data.map(x => x.result)
  }
  return {
    labels: labels,
    datasets: [
      {
        label: 'New TCC Apps',
        backgroundColor: '#0185b7',
        data: data
      }
    ]
  }
})

const totalTcc = computed(() => {
  let data
  let labels
  if (store.state.monthlyReportResponse !== null) {
    if (totalPeriod.value === 'Monthly') {
      data = store.state.monthlyReportResponse.totalTccMonthly
    } else if (totalPeriod.value === 'Quarterly') {
      data = store.state.monthlyReportResponse.totalTccQuarterly
    } else {
      data = store.state.monthlyReportResponse.totalTccYearly
    }
    if (store.state.selectedPartner.id === -1) {
      data = data.map(x => {
        return {
          id: -1,
          name: 'All',
          date: x[0].date,
          result: x.map(y => y.result).reduce((partialSum, a) => partialSum + a, 0)
        }
      })
    } else {
      data = data.map(x => x.find(y => y.id === store.state.selectedPartner.id))
    }
    labels = data.map(x => new Date(x.date).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }))
    data = data.map(x => x.result)
  }
  return {
    labels: labels,
    datasets: [
      {
        label: 'Total TCC Apps',
        backgroundColor: '#0185b7',
        data: data
      }
    ]
  }
})

const newTccPerPartner = computed(() => {
  let data = []
  if (store.state.monthlyReportResponse !== null) {
    if (newPerPartnerPeriod.value === 'Monthly') {
      data = store.state.monthlyReportResponse.tccMonthly
    } else if (newPerPartnerPeriod.value === 'Quarterly') {
      data = store.state.monthlyReportResponse.tccQuarterly
    } else {
      data = store.state.monthlyReportResponse.tccYearly
    }
    const index = data.map(x => x[0].date).findIndex(x => new Date(x).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) === newTccDate.value.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }))
    data = data.at(index).filter(x => x.result > 0).sort((a, b) => a.name.localeCompare(b.name))
  }
  return {
    labels: data.map(x => x.name),
    datasets: [
      {
        label: 'New TCC Apps',
        backgroundColor: '#0185b7',
        data: data.map(x => x.result)
      }
    ]
  }
})

const totalTccPerPartner = computed(() => {
  let data = []
  if (store.state.monthlyReportResponse !== null) {
    if (totalPerPartnerPeriod.value === 'Monthly') {
      data = store.state.monthlyReportResponse.totalTccMonthly
    } else if (totalPerPartnerPeriod.value === 'Quarterly') {
      data = store.state.monthlyReportResponse.totalTccQuarterly
    } else {
      data = store.state.monthlyReportResponse.totalTccYearly
    }
    const index = data.map(x => x[0].date).findIndex(x => new Date(x).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) === totalTccDate.value.toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }))
    data = data.at(index).filter(x => x.result > 0).sort((a, b) => a.name.localeCompare(b.name))
  }
  return {
    labels: data.map(x => x.name),
    datasets: [
      {
        label: 'New TCC Apps',
        backgroundColor: '#0185b7',
        data: data.map(x => x.result)
      }
    ]
  }
})

function onSelectNewPeriod (payload) {
  newPeriod.value = payload.period
}

function onSelectTotalPeriod (payload) {
  totalPeriod.value = payload.period
}

function selectNewTccDate (payload) {
  newTccDate.value = payload.date
}

function selectTotalTccDate (payload) {
  totalTccDate.value = payload.date
}

function onSelectNewPerPartnerPeriod (payload) {
  newPerPartnerPeriod.value = payload.period
}

function onSelectTotalPerPartnerPeriod (payload) {
  totalPerPartnerPeriod.value = payload.period
}

watch(newPerPartnerPeriod, () => {
  if (newPerPartnerPeriod.value === 'Monthly') {
    newTccDate.value = new Date(store.state.monthlyReportResponse.tccMonthly.at(-1)[0].date)
  } else if (newPerPartnerPeriod.value === 'Quarterly') {
    newTccDate.value = new Date(store.state.monthlyReportResponse.tccQuarterly.at(-1)[0].date)
  } else {
    newTccDate.value = new Date(store.state.monthlyReportResponse.tccYearly.at(-1)[0].date)
  }
})

watch(totalPerPartnerPeriod, () => {
  if (totalPerPartnerPeriod.value === 'Monthly') {
    totalTccDate.value = new Date(store.state.monthlyReportResponse.totalTccMonthly.at(-1)[0].date)
  } else if (totalPerPartnerPeriod.value === 'Quarterly') {
    totalTccDate.value = new Date(store.state.monthlyReportResponse.totalTccQuarterly.at(-1)[0].date)
  } else {
    totalTccDate.value = new Date(store.state.monthlyReportResponse.totalTccYearly.at(-1)[0].date)
  }
})
</script>

<script>
export default {
  name: 'Tcc',
  inheritAttrs: false
}
</script>

<style scoped>
.chart-container {
  width: 1220px;
  height: 560px;
}
</style>
